import { API_BASE_URL } from '@synergy/config';
import { Box, IconButton, Tooltip } from '@mui/material';
import { DocumentStatus } from '@synergy/models';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { LegacyDateRangeFilter, UrlFilter, ValueOrNDash } from '@synergy/components';
import {
    PaloAmount,
    PaloCancellationNotice,
    PaloColumn,
    PaloDeliveryDateValue,
    PaloFilterChips,
    PaloFilterModal,
    PaloListView,
    PaloStatusPill,
    usePaloListSort,
} from '@synergy/components-palo';
import { PdfIcon } from '@synergy/theme';
import { ReactElement, useEffect, useState } from 'react';
import { portalOrderStatus, portalOrderStatusTranslationId } from '@synergy/utils';
import { useSearchParams } from 'react-router-dom';
import urlJoin from 'url-join';

import * as PurchaseOrderActions from '../actions/purchase-order-actions';
import { CustomerFilter } from '../../../common/components/filter/customer-filter';
import { PurchaseOrder } from '../../../common/models/purchase-order';
import { useSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

const isCancelled = (order: PurchaseOrder): boolean => DocumentStatus.Canceled === order.status;

/**
 * Orders cancelled in the last 30 days, above the list, so a supplier does not
 * deliver what was called off (#2267).
 */
const RecentCancellationsNotice = ({ cancellations }: { cancellations: PurchaseOrder[] }): ReactElement | null => (
    <PaloCancellationNotice
        cancellations={cancellations}
        getId={order => order.id}
        title={<FormattedMessage id="app.purchaseOrders.cancelledNotice" values={{ count: cancellations.length }} />}
        describe={order => (
            <>
                {order.number} · {order.customerAccount?.name} ·{' '}
                <FormattedDate value={order.updatedAt} year="numeric" month="2-digit" day="2-digit" />
            </>
        )}
        pdfHref={order => urlJoin(API_BASE_URL, `/purchase-orders/${order.id}/pdf-purchase-order`)}
        pdfLabel={<FormattedMessage id="app.purchaseOrders.cancelledNotice.pdf" />}
    />
);

/** As the legacy filter, without its search field — the list header owns that. */
const PaloPurchaseOrdersFilter = (): ReactElement => (
    <PaloFilterModal>
        <LegacyDateRangeFilter />
        <CustomerFilter />
    </PaloFilterModal>
);

export const PaloPurchaseOrdersOverview = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    // The list opens in its own order, and asks the server for it: the header
    // showed "Nummer" with an arrow while the request carried no order at all,
    // so the oldest document stood on top under a header saying the newest
    // (LST-S16, #2234).
    const { sort, params: sortedParams } = usePaloListSort('purchase-orders');
    const data = useSelector(state => state.purchaseOrderData.data);
    const requesting = useSelector(state => state.purchaseOrderData.requestingPurchaseOrderData);
    const currency = useSelector(state => state.settings.currency);

    const [recentCancellations, setRecentCancellations] = useState<PurchaseOrder[]>([]);

    useEffect(() => {
        dispatch(PurchaseOrderActions.fetchRecentCancellations()).then(setRecentCancellations);
    }, [dispatch]);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(PurchaseOrderActions.fetchPurchaseOrders(sortedParams));

        return () => {
            dispatch(PurchaseOrderActions.resetPurchaseOrders());
        };
    }, [dispatch, sortedParams]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const columns: PaloColumn<PurchaseOrder>[] = [
        {
            key: 'number',
            type: 'documentNumber',
            head: <FormattedMessage id="app.table.column.number" />,
            sortKey: 'number',
            cell: order => order.number,
        },
        {
            key: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: order => (
                <PaloStatusPill
                    status={portalOrderStatus(order.status)}
                    label={<FormattedMessage id={portalOrderStatusTranslationId(order.status)} />}
                />
            ),
        },
        {
            key: 'customerAccount',
            type: 'name',
            head: <FormattedMessage id="app.table.column.customer" />,
            cell: order => order.customerAccount?.name,
        },
        {
            key: 'deliveryDate',
            type: 'deliveryDate',
            head: <FormattedMessage id="app.table.column.deliveryDate" />,
            cell: order => (
                <PaloDeliveryDateValue
                    date={order.deliveryDate}
                    type={order.deliveryDateType}
                    void={isCancelled(order)}
                />
            ),
        },
        {
            key: 'publishDate',
            type: 'date',
            head: <FormattedMessage id="app.table.column.publishDate" />,
            sortKey: 'createdAt',
            cell: order => (
                <ValueOrNDash
                    value={order.createdAt}
                    valueRender={value => <FormattedDate value={value} year="numeric" month="short" day="2-digit" />}
                    strictEqual={true}
                />
            ),
        },
        {
            key: 'totalNetPrice',
            currency,
            type: 'amount',
            head: <FormattedMessage id="app.table.column.totalNetPrice" />,
            cell: order => (
                <Box component="span" sx={{ textDecoration: isCancelled(order) ? 'line-through' : undefined }}>
                    <PaloAmount value={order.totalNetPrice} />
                </Box>
            ),
        },
        {
            key: 'pdf',
            type: 'icon',
            head: '',
            cell: order => (
                <Tooltip title={<FormattedMessage id="app.label.download" />} placement="top">
                    <a
                        download={`purchase-order-${order.id}.pdf`}
                        href={urlJoin(API_BASE_URL, `/purchase-orders/${order.id}/pdf-purchase-order`)}
                        rel="noopener noreferrer"
                        target="_blank"
                        onClick={event => event.stopPropagation()}
                    >
                        <IconButton size="small">
                            <PdfIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    </a>
                </Tooltip>
            ),
        },
    ];

    return (
        <UrlFilter useLegacyFilter={true}>
            <PaloListView<PurchaseOrder>
                title={<FormattedMessage id="app.navigation.purchases" />}
                filters={<PaloPurchaseOrdersFilter />}
                filterChips={<PaloFilterChips />}
                sort={sort}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                searchValue={searchParams.get('search') ?? ''}
                searchPlaceholder={intl.formatMessage({ id: 'app.common.searchPlaceholder' })}
                onSearchChange={value => {
                    const next = new URLSearchParams(searchParams);
                    if (value) {
                        next.set('search', value);
                    } else {
                        next.delete('search');
                    }
                    next.set('page', '1');
                    setSearchParams(next, { replace: true });
                }}
                columns={columns}
                items={data.items}
                getRowId={order => order.id}
                isRowCritical={isCancelled}
                notice={<RecentCancellationsNotice cancellations={recentCancellations} />}
                requesting={requesting}
                emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                pagination={{
                    count: data.total,
                    page,
                    limit,
                    onPageChange: newPage => updateParams({ page: String(newPage) }),
                    onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                }}
            />
        </UrlFilter>
    );
};
